import { useMemo, useRef, useState } from "react";
import {
  ColumnDirective,
  ColumnsDirective,
  KanbanComponent,
  SortSettingsModel,
} from "@syncfusion/ej2-react-kanban";

import {
  JobPipelineCandidateSheet,
  JobPipelineEmptyStateForFilters,
  JobPipelineKanbanCard,
  JobPipelineKanbanColumnHeader,
} from "@/components";

import { useSuccessToast } from "@/hooks";
import { useUpdateCandidacy } from "@/hooks/mutations";
import { useSetCandidacyInCandidacies } from "@/hooks/sets";
import { candidacyStatusToLabel, candidacyStatusToRank } from "@/lib/constants";
import {
  CandidaciesSortKeyAndOrder,
  Candidacy,
  CandidacyStatus,
  CandidacyWithProfileResponse,
  FlatCandidacyWithProfileResponse,
} from "@/types";

const sortToSortSettingsModel: Record<
  CandidaciesSortKeyAndOrder,
  SortSettingsModel
> = {
  [CandidaciesSortKeyAndOrder.TimeInStageAsc]: {
    field: "timeInStage",
    direction: "Descending",
    sortBy: "Custom",
  },
  [CandidaciesSortKeyAndOrder.TimeInStageDesc]: {
    direction: "Ascending",
    field: "timeInStage",
    sortBy: "Custom",
  },
  [CandidaciesSortKeyAndOrder.ScoreDesc]: {
    direction: "Descending",
    field: "candidacyScore",
    sortBy: "Custom",
  },
};

export const JobPipelineKanban = ({
  candidaciesWithProfiles,
  sort,
}: {
  candidaciesWithProfiles: CandidacyWithProfileResponse[];
  sort: CandidaciesSortKeyAndOrder;
}) => {
  const [selectedCandidacyIndex, setSelectedCandidacyIndex] = useState<
    number | null
  >(null);
  const setCandidacyInCandidacies = useSetCandidacyInCandidacies();
  const successToast = useSuccessToast();
  const { mutate: updateCandidacy } = useUpdateCandidacy();

  const isEmptyStateForFilters = candidaciesWithProfiles.length === 0;

  // Needed because Syncfusion Kanban doesn't support nested data.
  const flatAndFullCandidacyWithProfileResponses: {
    flat: FlatCandidacyWithProfileResponse;
    full: CandidacyWithProfileResponse;
  }[] = useMemo(() => {
    const flatAndFull = candidaciesWithProfiles.map((candidacyWithProfile) => ({
      flat: {
        candidacyId: candidacyWithProfile.candidacy.id,
        candidacyStatus: candidacyWithProfile.candidacy.status,
        candidacyTalentId: candidacyWithProfile.candidacy.talentId,
        candidateProfileDesiredPosition:
          candidacyWithProfile.candidateProfile.profile.desiredPosition,
        candidateProfileFirstName:
          candidacyWithProfile.candidateProfile.profile.firstName,
        candidateProfileLastName:
          candidacyWithProfile.candidateProfile.profile.lastName,
        candidateProfileLocation:
          candidacyWithProfile.candidateProfile.profile.location,
        candidacyScore: candidacyWithProfile.candidacyScore ?? 0, // Need to map to zero so Syncfusion can handle the sort...
        timeInStage: candidacyWithProfile.timeInStage,
      },
      full: candidacyWithProfile,
    }));

    flatAndFull.sort((a, b) => {
      if (a.flat.candidacyStatus !== b.flat.candidacyStatus) {
        return (
          candidacyStatusToRank[a.flat.candidacyStatus] -
          candidacyStatusToRank[b.flat.candidacyStatus]
        );
      }

      if (sort === CandidaciesSortKeyAndOrder.TimeInStageAsc) {
        return (
          new Date(b.flat.timeInStage).getTime() -
          new Date(a.flat.timeInStage).getTime()
        );
      } else if (sort === CandidaciesSortKeyAndOrder.TimeInStageDesc) {
        return (
          new Date(a.flat.timeInStage).getTime() -
          new Date(b.flat.timeInStage).getTime()
        );
      } else {
        return b.flat.candidacyScore - a.flat.candidacyScore;
      }
    });

    return flatAndFull;
  }, [candidaciesWithProfiles, sort]);

  const fromStatusRef = useRef<CandidacyStatus | null>(null);

  const dragStart = ({
    data,
  }: {
    data?: FlatCandidacyWithProfileResponse[];
  }) => {
    if (data?.length) {
      fromStatusRef.current = data[0].candidacyStatus;
    }
  };

  const dragStop = ({
    data,
  }: {
    data?: FlatCandidacyWithProfileResponse[];
  }) => {
    if (data?.length && data[0].candidacyStatus !== fromStatusRef.current) {
      updateCandidacy(
        {
          candidacyId: data[0].candidacyId,
          updateCandidacyRequest: { status: data[0].candidacyStatus },
        },
        {
          onSuccess: (candidacy: Candidacy) => {
            successToast({ description: "Candidacy updated!" });
            setCandidacyInCandidacies({
              candidacy,
              timeInStage: new Date().toISOString(),
            });
          },
        },
      );
    }
    fromStatusRef.current = null;
  };

  return (
    <div>
      {isEmptyStateForFilters ? (
        <JobPipelineEmptyStateForFilters className="border-200 rounded-lg border" />
      ) : (
        <KanbanComponent
          id="kanban"
          keyField="candidacyStatus"
          cardClick={(args) => {
            flatAndFullCandidacyWithProfileResponses.find(
              (flatAndFull, index) => {
                if (
                  flatAndFull.flat.candidacyTalentId ===
                  args.data.candidacyTalentId
                ) {
                  setSelectedCandidacyIndex(index);
                  return true;
                }
                return false;
              },
            );
          }}
          cardSettings={{
            headerField: "candidacyTalentId",
            template: JobPipelineKanbanCard,
          }}
          dataSource={flatAndFullCandidacyWithProfileResponses.map(
            ({ flat }) => flat,
          )}
          dragStart={dragStart}
          dragStop={dragStop}
          sortSettings={sortToSortSettingsModel[sort]}
        >
          <ColumnsDirective>
            {Object.values(CandidacyStatus).map((status) => (
              <ColumnDirective
                allowDrop={status !== CandidacyStatus.REJECTED}
                headerText={candidacyStatusToLabel[status]}
                key={status}
                keyField={status}
                template={JobPipelineKanbanColumnHeader}
              />
            ))}
          </ColumnsDirective>
        </KanbanComponent>
      )}
      <JobPipelineCandidateSheet
        candidaciesWithProfiles={flatAndFullCandidacyWithProfileResponses.map(
          ({ full }) => full,
        )}
        selectedCandidacyIndex={selectedCandidacyIndex}
        setSelectedCandidacyIndex={setSelectedCandidacyIndex}
      />
    </div>
  );
};
